// Analytics utility for tracking page views
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://youngpresso-api-73d540a3cc99.herokuapp.com';

/**
 * Track a page view (completely non-blocking)
 * @param {string} page - Page name ('home' or 'pricing')
 * @param {string} referrer - Optional referrer URL
 */
export const trackPageView = (page, referrer = null) => {
  // Run in next tick to ensure it's completely non-blocking
  setTimeout(async () => {
    try {
      // Validate page parameter
      if (!['home', 'pricing'].includes(page)) {
        console.warn('Invalid page parameter for analytics:', page);
        return;
      }

      // Get referrer from document if not provided
      const actualReferrer = referrer || document.referrer || window.location.href;

      // Use fetch with timeout and no-cors for maximum compatibility
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const response = await fetch(`${API_BASE_URL}/api/analytics/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          page,
          referrer: actualReferrer
        }),
        signal: controller.signal,
        // Add these for better compatibility
        mode: 'cors',
        cache: 'no-cache'
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const result = await response.json();
        if (!result.success) {
          console.warn('Analytics tracking failed:', result.message);
        }
      } else {
        console.warn('Analytics tracking failed with status:', response.status);
      }

    } catch (error) {
      // Fail completely silently to not break the user experience
      if (error.name !== 'AbortError') {
        console.warn('Analytics tracking error:', error.message);
      }
    }
  }, 0); // Run in next tick
};

/**
 * Track page view with retry mechanism (non-blocking)
 * @param {string} page - Page name ('home' or 'pricing')
 * @param {number} maxRetries - Maximum number of retry attempts
 */
export const trackPageViewWithRetry = (page, maxRetries = 2) => {
  // Run completely in background, don't return a promise
  setTimeout(async () => {
    let attempts = 0;
    
    while (attempts <= maxRetries) {
      try {
        // Since trackPageView is now synchronous (returns immediately), 
        // we need to implement the actual tracking logic here
        await performTrackingRequest(page);
        return; // Success, exit the retry loop
      } catch (error) {
        attempts++;
        if (attempts > maxRetries) {
          console.warn(`Analytics tracking failed after ${maxRetries} retries for page: ${page}`);
          return;
        }
        
        // Wait before retrying (exponential backoff)
        const delay = Math.pow(2, attempts) * 1000; // 2s, 4s, 8s...
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }, 0);
};

/**
 * Internal function to perform the actual tracking request
 * @param {string} page - Page name
 * @param {string} referrer - Optional referrer URL
 */
const performTrackingRequest = async (page, referrer = null) => {
  // Validate page parameter
  if (!['home', 'pricing'].includes(page)) {
    throw new Error('Invalid page parameter');
  }

  // Get referrer from document if not provided
  const actualReferrer = referrer || document.referrer || window.location.href;

  // Use fetch with timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

  const response = await fetch(`${API_BASE_URL}/api/analytics/track`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      page,
      referrer: actualReferrer
    }),
    signal: controller.signal,
    mode: 'cors',
    cache: 'no-cache'
  });

  clearTimeout(timeoutId);

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const result = await response.json();
  if (!result.success) {
    throw new Error(result.message);
  }

  return result;
};

/**
 * Track page view when component mounts (React hook style) - completely non-blocking
 * @param {string} page - Page name ('home' or 'pricing')
 */
export const usePageViewTracking = (page) => {
  // This will be called in useEffect in the components
  const trackOnMount = () => {
    // Use requestIdleCallback if available, otherwise setTimeout
    if (window.requestIdleCallback) {
      window.requestIdleCallback(() => {
        trackPageView(page);
      }, { timeout: 2000 });
    } else {
      // Fallback to setTimeout with longer delay to ensure page is fully loaded
      setTimeout(() => {
        trackPageView(page);
      }, 1500);
    }
  };
  
  return trackOnMount;
};

/**
 * Check if analytics API is available
 */
export const checkAnalyticsHealth = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/analytics/health`);
    const result = await response.json();
    return result.success;
  } catch (error) {
    console.warn('Analytics health check failed:', error.message);
    return false;
  }
};

export default {
  trackPageView,
  trackPageViewWithRetry,
  usePageViewTracking,
  checkAnalyticsHealth
};
